import { useEffect, useState, useCallback } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, Building2, LogOut, ChevronLeft, CreditCard,
  IndianRupee, Receipt, Clock, Bell, LifeBuoy, Menu, Users, Crosshair, Mail, Activity, ShieldCheck,
  KeyRound, X, Eye, EyeOff,
} from 'lucide-react';
import { getNewLeadsCount, changePassword } from '@/services/apiClient';
import { hasSAPermission } from '@/constants/saPermissions';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import ThemeToggle from '@/components/ThemeToggle';
import EchoHireLogo from '@/components/branding/EchoHireLogo';
import NotificationBell from '@/components/superadmin/NotificationBell';
import { useLogoutConfirm } from '@/components/ux/useLogoutConfirm';

// saKey: the SA permission key required to see this item.
// null = only super_admin (role check), undefined = always visible to both
const NAV_ITEMS = [
  { label: 'Overview',        to: '/super-admin',                    icon: LayoutDashboard, end: true,  saKey: 'sa_overview' },
  { label: 'Leads',           to: '/super-admin/leads',              icon: Users,           end: false, saKey: 'sa_leads',           badge: 'leads' },
  { label: 'Companies',       to: '/super-admin/companies',          icon: Building2,       end: false, saKey: 'sa_companies' },
  { label: 'Plans',           to: '/super-admin/plans',              icon: CreditCard,      end: false, saKey: 'sa_plans' },
  { label: 'Earnings',        to: '/super-admin/earnings',           icon: IndianRupee,     end: false, saKey: 'sa_earnings' },
  { label: 'Billing',         to: '/super-admin/billing',            icon: Receipt,         end: false, saKey: 'sa_billing' },
  { label: 'Trials',          to: '/super-admin/trials',             icon: Clock,           end: false, saKey: 'sa_trials' },
  { label: 'Notifications',   to: '/super-admin/notifications',      icon: Bell,            end: false, saKey: 'sa_notifications' },
  { label: 'Support',         to: '/super-admin/support',            icon: LifeBuoy,        end: false, saKey: 'sa_support' },
  { label: 'Prospects',       to: '/super-admin/prospects',          icon: Crosshair,       end: false, saKey: 'sa_prospects' },
  { label: 'Email Templates', to: '/super-admin/email-templates',    icon: Mail,            end: false, saKey: 'sa_email_templates' },
  { label: 'System Health',   to: '/super-admin/health',             icon: Activity,        end: false, saKey: 'sa_health' },
  // Internal Team: only visible to true super_admin (saKey: null means role=super_admin only)
  { label: 'Internal Team',   to: '/super-admin/team',               icon: ShieldCheck,     end: false, saKey: null },
];

function getUserFromStorage() {
  try {
    return JSON.parse(localStorage.getItem('user'));
  } catch {
    return null;
  }
}

function SidebarContent({ collapsed, onNavigate, onLogout, onChangePassword, user, onToggleCollapse, newLeadsCount }) {
  const initials =
    user?.firstName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'S';

  // Filter nav items based on role/saPermissions
  const visibleNavItems = NAV_ITEMS.filter(item => {
    if (!user) return false;
    if (user.role === 'super_admin') return true; // full access
    if (user.role === 'echohire_staff') {
      if (item.saKey === null) return false; // super_admin-only items
      return hasSAPermission(user, item.saKey);
    }
    return false;
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        className={cn(
          'flex shrink-0 items-center gap-1.5 overflow-hidden border-b border-border px-2 py-2',
          collapsed ? 'flex-col' : 'flex-row'
        )}
      >
        {onToggleCollapse && (
          <Button
            variant="outline"
            size="icon"
            className={cn(
              'h-9 w-9 shrink-0 border-border bg-background text-foreground shadow-none',
              'hover:bg-muted hover:text-foreground',
              collapsed && 'order-first'
            )}
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronLeft className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')} />
          </Button>
        )}
        <NavLink
          to="/super-admin"
          onClick={onNavigate}
          className={cn(
            'flex min-w-0 overflow-hidden py-0.5',
            collapsed ? 'w-full justify-center' : 'flex-1'
          )}
        >
          <EchoHireLogo collapsed={collapsed} size="compact" />
        </NavLink>
      </div>

      {!collapsed && (
        <p className="shrink-0 px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
          Platform
        </p>
      )}
      <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 pb-2 pt-1">
        {visibleNavItems.map(({ label, to, icon: Icon, end, badge }) => {
          const badgeCount = badge === 'leads' ? newLeadsCount : 0;
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md py-2 text-[13px] font-medium transition-colors',
                  collapsed ? 'justify-center px-2' : 'border-l-2 pl-2.5 pr-2',
                  collapsed
                    ? isActive
                      ? 'bg-muted/70 text-foreground'
                      : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                    : isActive
                      ? 'border-primary bg-muted/70 text-foreground'
                      : 'border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                )
              }
            >
              <span className="relative shrink-0">
                <Icon className="h-[18px] w-[18px]" aria-hidden />
                {badgeCount > 0 && collapsed && (
                  <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-white">
                    {badgeCount > 9 ? '9+' : badgeCount}
                  </span>
                )}
              </span>
              {!collapsed && <span className="flex-1">{label}</span>}
              {!collapsed && badgeCount > 0 && (
                <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white">
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="mt-auto shrink-0 space-y-2 border-t border-border p-2">
        <div className={cn('flex items-center gap-2 px-1', collapsed ? 'flex-col' : 'justify-between')}>
          {!collapsed && <span className="text-[11px] font-medium text-muted-foreground">Appearance</span>}
          <ThemeToggle size="small" />
        </div>
        <div
          className={cn(
            'flex flex-col gap-2 rounded-md border border-border/60 bg-muted/30 p-2',
            collapsed && 'items-center border-0 bg-transparent p-1'
          )}
        >
          <div className={cn('flex min-w-0 items-center gap-2', collapsed && 'flex-col')}>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary text-xs font-semibold text-primary-foreground">
              {initials}
            </div>
            {!collapsed && user && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground">
                  {user.firstName} {user.lastName}
                </p>
                <p className="truncate text-[11px] text-muted-foreground">
                  {user.role === 'echohire_staff' ? 'EchoHire Staff' : 'Super admin'}
                </p>
              </div>
            )}
            {!collapsed && (
              <button
                type="button"
                title="Change password"
                onClick={onChangePassword}
                className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <KeyRound className="h-4 w-4" />
              </button>
            )}
          </div>
          {collapsed && (
            <button
              type="button"
              title="Change password"
              onClick={onChangePassword}
              className="flex w-full items-center justify-center rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <KeyRound className="h-4 w-4" />
            </button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'h-9 w-full gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive',
              collapsed && 'flex-col gap-1 px-2 py-2.5 h-auto'
            )}
            onClick={onLogout}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span className={cn('text-sm font-medium', collapsed && 'text-[11px]')}>Logout</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Change Password Modal ─────────────────────────────────────────────────────
/**
 * Defined at module scope, deliberately.
 *
 * This lived inside ChangePasswordModal, so every keystroke re-rendered the
 * modal, produced a new function identity for the component, and made React
 * treat it as a different component type at that position — unmounting the
 * input and mounting a fresh one. The visible effect was losing focus after
 * each character, so a password had to be typed one click at a time.
 */
function PasswordField({ id, label, value, visible, onChange, onToggle }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
      <div className="flex items-center overflow-hidden rounded-lg border border-border bg-background focus-within:ring-2 focus-within:ring-primary/40">
        <input
          id={id}
          name={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(id, e.target.value)}
          className="flex-1 bg-transparent px-3 py-2 text-sm text-foreground focus:outline-none"
          placeholder={'\u2022'.repeat(8)}
          autoComplete={id === 'currentPassword' ? 'current-password' : 'new-password'}
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={visible ? 'Hide password' : 'Show password'}
          className="px-3 text-muted-foreground hover:text-foreground"
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [show, setShow] = useState({ current: false, new: false, confirm: false });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const updateField = useCallback((id, value) => {
    setForm(f => ({ ...f, [id]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.newPassword.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (form.newPassword !== form.confirmPassword) { setError('New passwords do not match.'); return; }
    setLoading(true);
    try {
      await changePassword({ currentPassword: form.currentPassword, newPassword: form.newPassword });
      setSuccess(true);
      setTimeout(onClose, 1800);
    } catch (err) {
      setError(err.message || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-sm rounded-xl border border-border bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Change Password</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5">
          {success ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15">
                <KeyRound className="h-5 w-5 text-emerald-600" />
              </div>
              <p className="text-sm font-semibold text-foreground">Password changed successfully!</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-lg bg-red-500/10 border border-red-400/30 p-3 text-xs text-red-600 dark:text-red-400">{error}</div>
              )}
              <PasswordField
                id="currentPassword" label="Current Password"
                value={form.currentPassword} visible={show.current}
                onChange={updateField}
                onToggle={() => setShow(v => ({ ...v, current: !v.current }))}
              />
              <PasswordField
                id="newPassword" label="New Password"
                value={form.newPassword} visible={show.new}
                onChange={updateField}
                onToggle={() => setShow(v => ({ ...v, new: !v.new }))}
              />
              <PasswordField
                id="confirmPassword" label="Confirm New Password"
                value={form.confirmPassword} visible={show.confirm}
                onChange={updateField}
                onToggle={() => setShow(v => ({ ...v, confirm: !v.confirm }))}
              />
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Updating…' : 'Update Password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SuperAdminLayout() {
  const { requestLogout, LogoutConfirmDialog } = useLogoutConfirm();
  const [user, setUser] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const [showChangePwd, setShowChangePwd] = useState(false);

  const fetchLeadsBadge = useCallback(async () => {
    try {
      const data = await getNewLeadsCount();
      setNewLeadsCount(data.count || 0);
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    setUser(getUserFromStorage());
    fetchLeadsBadge();
    const interval = setInterval(fetchLeadsBadge, 60_000); // refresh every minute
    return () => clearInterval(interval);
  }, [fetchLeadsBadge]);

  return (
    <div className="flex h-dvh max-h-dvh min-h-0 w-full overflow-hidden bg-background">
      {LogoutConfirmDialog}
      {showChangePwd && <ChangePasswordModal onClose={() => setShowChangePwd(false)} />}
      <aside
        className={cn(
          'hidden h-full max-h-dvh shrink-0 flex-col border-r border-border bg-card transition-[width] duration-200 md:flex',
          collapsed ? 'w-[88px]' : 'w-64'
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <SidebarContent
            collapsed={collapsed}
            onToggleCollapse={() => setCollapsed((c) => !c)}
            onLogout={requestLogout}
            onChangePassword={() => setShowChangePwd(true)}
            user={user}
            newLeadsCount={newLeadsCount}
          />
        </div>
      </aside>

      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex h-dvh w-64 flex-col border-r border-border bg-card transition-transform duration-200 md:hidden',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <SidebarContent collapsed={false} onNavigate={() => setMobileOpen(false)} onLogout={requestLogout} onChangePassword={() => { setMobileOpen(false); setShowChangePwd(true); }} user={user} newLeadsCount={newLeadsCount} />
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <header className="relative z-50 flex h-12 shrink-0 items-center justify-between gap-2 border-b border-border bg-card/95 px-4 backdrop-blur">
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground md:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="hidden text-sm font-medium text-muted-foreground md:block">Super Admin</span>
          <div className="ml-auto flex items-center gap-1">
            <NotificationBell />
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-3 sm:p-5 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
